import { defineStore } from 'pinia';

import { useLocalStorage } from '@vueuse/core';
import { showToast } from '@/utilities/toast-handler';
import { useAuthStore } from '@/modules/auth/store/index';
import {
    setSsoAttemptId,
    getSsoAttemptId,
    setSsoEmail,
    sendSsoTelemetry,
    prepareFreshSsoLogin,
    purgeAmsClientState,
} from '@/modules/sso/utils/ssoTelemetry';

const SSO_AUTH_URL = process.env.VUE_APP_API_URL + '/auth/sso';
const SSO_AVAILABILITY = process.env.VUE_APP_API_URL + '/auth/available';


export const useSSOStore = defineStore('ssoAuth', {
    state: () => ({
        user: useLocalStorage('ssoUser', {}),
        token: useLocalStorage('ssoToken', null),
        email: null,
        loader: false,
        showLoginFields: false, // Tracks whether to show login fields
    }),

    getters: {
        isAuthenticated: (state) => !!state.token,
    },

    actions: {
        async checkSSO() {
            if (!this.email) {
                showToast({}, 'Please enter your email');
                return;
            }

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(this.email)) {
                showToast({ severity: "warn", summary: "Invalid Email" }, "Please enter a valid email address");
                return;
            }

            // Fresh start: reset Pinia then wipe origin storage, but keep the typed email.
            const email = this.email;
            const auth = useAuthStore();
            purgeAmsClientState(auth, this, { keepEmail: true });
            this.email = email;
            prepareFreshSsoLogin(email);

            this.loader = true;
            try {
                const response = await fetch(SSO_AVAILABILITY, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                    body: JSON.stringify({
                        email,
                        platform: 'ams',
                    }),
                });
                const result = await response.json();

                if (result?.sso_attempt_id) {
                    setSsoAttemptId(result.sso_attempt_id);
                }

                if (result?.data) {
                    await this.initiateSSO(email);
                } else {
                    this.loader = false;
                    this.showLoginFields = true;
                    sendSsoTelemetry('fe_error', {
                        email,
                        failure_reason: 'SSO not available for this email — falling back to password login',
                        backend_status: String(result?.data),
                        http_status: response.status,
                    });
                }
            } catch (error) {
                this.loader = false;
                this.showLoginFields = true;
                sendSsoTelemetry('fe_error', {
                    email,
                    failure_reason: 'SSO availability request failed on frontend',
                    error_name: error?.name || 'Error',
                    detail: error?.message || 'checkSSO fetch failed',
                });
            }
        },

        async initiateSSO(emailOverride = null) {
            const email = emailOverride || this.email;
            if (!email) {
                this.loader = false;
                showToast({ severity: "warn", summary: "Alert" }, "Please enter an email for SSO");
                return;
            }

            this.email = email;
            setSsoEmail(email);
            this.loader = true;

            try {
                const response = await fetch(SSO_AUTH_URL, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                    },
                    body: JSON.stringify({
                        email,
                        platform: 'ams',
                        sso_attempt_id: getSsoAttemptId() || undefined,
                    })
                });

                const result = await response.json();
                if (result?.sso_attempt_id) {
                    setSsoAttemptId(result.sso_attempt_id);
                }

                if (result.url) {
                    // Full navigation to Microsoft — leave loader on for a smooth handoff
                    window.location.href = result.url;
                } else {
                    this.loader = false;
                    showToast({ severity: "warn", summary: "Sign-in" }, "Unable to start Microsoft sign-in. Please try again or use password login.");
                    this.showLoginFields = true;
                    sendSsoTelemetry('fe_redirect_url_missing', {
                        email,
                        failure_reason: 'Backend /auth/sso returned no Microsoft redirect URL (url=null)',
                        http_status: response.status,
                        backend_message: result?.message || null,
                    });
                }
            } catch (error) {
                this.loader = false;
                showToast({ severity: "warn", summary: "Sign-in" }, "Something went wrong starting sign-in. Please try again.");
                sendSsoTelemetry('fe_error', {
                    email,
                    failure_reason: 'Frontend failed while requesting Microsoft redirect URL',
                    error_name: error?.name || 'Error',
                    detail: error?.message || 'initiateSSO fetch failed',
                });
            }
        },



    }
});
